/**
 * Order totals summary following reware-admin's Footer: current charges
 * (subtotal, promotions, shipping, surcharge, tax, total), a payment-method
 * caption, and refunds. Amounts are in the order's currency.
 *
 * Gift-card tenders are deliberately not shown yet. `paymentGiftCards`,
 * `refundedGiftCards` and `csGiftCards` are on the record but unrendered, so a
 * gift-card-paid order currently shows a charge breakdown with no indication
 * that a gift card covered part of it.
 */
import { Box, Divider, Stack, Typography } from '@mui/material';

import { SectionLabel } from '@src/components/indexPage';

import { formatCardLastFour, formatMoney, taxLineCents } from './format';
import { present } from './types';
import type { OrderRecord } from './types';

const Row = ({
  label,
  value,
  bold,
  negative,
}: {
  label: string;
  value: string;
  bold?: boolean;
  negative?: boolean;
}) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, color: negative ? 'error.main' : 'text.primary' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: bold ? 700 : 400, fontVariantNumeric: 'tabular-nums', color: negative ? 'error.main' : 'text.primary' }}
    >
      {value}
    </Typography>
  </Stack>
);

export const OrderTotals = ({ order }: { order: OrderRecord }) => {
  const currency = order.transactionCurrencyCode;
  const payment = order.paymentDetails;
  const money = (cents: number | null | undefined) => formatMoney(cents, currency);

  const subtotal = payment?.productAmt ?? order.amount;
  // KNOWN GAP — the two sides of this `??` are different currency bases.
  //
  // `order.shipping` is a custom resolver over ShippingService.cost, which falls back to
  // `order.shipping_amount` — the partner's primary-currency column. `payment.shippingAmt` reads
  // `shipping_amount_transaction`. Everything else on this card (subtotal, tax, total, and the
  // rest of paymentDetails) is built from `*_transaction` columns, so Shipping is the one row
  // that can disagree with its neighbours on a converted order, and which of the two it takes
  // decides whether it does.
  //
  // Fixing it needs a currency-tagged shipping field, or `shipping_amount_transaction` exposed on
  // OrderAdmin — today only `shippingTaxAmountTransaction` is.
  const shipping = order.shipping ?? payment?.shippingAmt;
  // Only read when there are no tax lines to itemise — when there are, each renders its own Row
  // below. No reduce here: it would only ever be computed and discarded.
  const tax = order.sales_tax;
  const refunded = payment?.refundedTotal ?? 0;

  return (
    <Box sx={{ maxWidth: 360 }}>
      <SectionLabel>Totals</SectionLabel>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'grey.50' }}>
        <Row label="Subtotal" value={money(subtotal)} />
        {present(order.promotionDiscounts).map(promo => (
          <Row key={promo.code ?? promo.name} label={`Promo ${promo.name ?? promo.code ?? ''}`} value={`-${money(promo.amount)}`} negative />
        ))}
        {/* Only an explicit 0 is "Free"; a null shipping amount is unknown, and saying Free
            asserts something stronger than the data supports. */}
        <Row label="Shipping" value={shipping == null ? '—' : (shipping === 0 ? 'Free' : money(shipping))} />
        {!!order.shippingSurcharge && <Row label="Oversize Surcharge" value={money(order.shippingSurcharge)} />}
        {order.taxLines.map((line, index) => (
          <Row
            // Shopify tax lines routinely share a title (per-jurisdiction lines), so the title
            // alone is not a stable key.
            key={`${line.title ?? 'tax'}-${index}`}
            label={`${line.title ?? 'Tax'}${line.rate != null ? ` (${(line.rate * 100).toFixed(1)}%)` : ''}`}
            value={money(taxLineCents(line.price))}
          />
        ))}
        {!order.taxLines.length && <Row label="Sales Tax" value={money(tax)} />}
        <Divider sx={{ my: 0.75 }} />
        <Row label="Order Total" value={money(order.total)} bold />
        {refunded > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Row label="Refunded" value={`-${money(refunded)}`} negative />
            <Row label="Remaining" value={money(payment?.remainingTotal ?? (order.total ?? 0) - refunded)} bold />
          </>
        )}
        {!!payment && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {payment.ccBrand ?
                `Paid with ${payment.ccBrand} ···· ${formatCardLastFour(payment.ccLast4)}` :
                `Paid via ${payment.fundingSource ?? 'card'}`}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default OrderTotals;
